import { Theme } from '@core/enums';
import { Device, until } from '@helpers/media';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import Button from '@stories/Elements/Forms/Buttons/Button';
import Image from '@stories/Elements/Media/Image/Image';
import React, { FunctionComponent } from 'react';
import { SystemCardStyles } from './SystemCard.styles';

export interface SystemCardModel {
  /**
   * Cards have the ability to set an individual theme colour
   */
  theme: Theme;
  id: string;
  iconUrl?: string;
  iconAlt?: string;
  imageUrl: string;
  imageAlt: string;
  text: string;
  url: string;
  buttonText?: string;
}

export interface SystemCardProps {
  theme: Theme;
  system: SystemCardModel;
  index?: number;
  /**
   * Optional image sizes can be passed in and set accordingly
   */
  imageSizes?: string;
  /**
   * Used to set the delayInterval for animation based of how many items in a given row
   */
  rowItems?: number;
}

const SystemCard: FunctionComponent<SystemCardProps> = ({
  theme,
  system,
  index = 0,
  imageSizes = '100vw',
  rowItems = useMedia([until(Device.Tablet), until(Device.TabletLarge), until(Device.DesktopSmall)], [1, 2, 3], 4),
}) => {
  const [elementRef, inView] = useInView<HTMLAnchorElement>({ threshold: 0.1 });

  const baseDelay = 0;
  const indexDelay = index % rowItems;
  const delayInterval = baseDelay + indexDelay * 100;

  // set button theme to red if card theme is black, otherwise display black button
  const buttonTheme = theme === Theme.Black ? 'red' : 'black-grey';

  return (
    <SystemCardStyles.Card
      href={system.url}
      ref={elementRef}
      inView={inView}
      delay={delayInterval}
      theme={theme ?? system.theme}
    >
      <SystemCardStyles.ImageWrapper>
        <Image
          Component={SystemCardStyles.Image}
          src={system.imageUrl}
          alt={system.imageAlt}
          sizes={imageSizes}
          loading="lazy"
        />
      </SystemCardStyles.ImageWrapper>
      <SystemCardStyles.Text>
        {system.iconUrl && system.iconAlt && (
          <SystemCardStyles.Icon src={system.iconUrl} alt={system.iconAlt} loading="lazy" />
        )}
        <h3>{system.text}</h3>
        {system.buttonText && (
          <SystemCardStyles.ButtonWrapper>
            <Button theme={buttonTheme} text={system.buttonText} fakeButton={true} icon={'arrow'} />
          </SystemCardStyles.ButtonWrapper>
        )}
      </SystemCardStyles.Text>
    </SystemCardStyles.Card>
  );
};

export default SystemCard;
